import cn from 'clsx'
import { replace } from 'connected-react-router'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import Pane from 'components/Common/Pane'
import OfferPageGallery from '../OfferPageGallery'
import PackageOptions from './FlashHotelPackageOptions'

import { setRecentlyUsedAirportCode } from 'actions/AuthActions'
import { clearBookingCalendar, fetchCalendars, fetchCalendarsByOccupancy } from 'actions/CalendarActions'
import { fetchOfferFlightPrice, fetchRelatedTravelItems } from 'actions/OfferActions'
import { CLEAR_OFFER_CHECK_IN_CHECK_OUT, SET_OFFER_CHECK_IN_CHECK_OUT } from 'actions/actionConstants'

import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Subtitle from 'LuxKit/Typography/Subtitle'
import DesktopRelatedTravelItems from 'components/OfferPage/RelatedTravelItems'
import ExpiredDealsLereRecommendations from 'components/Recommendations/ExpiredDeals/ExpiredDealsLereRecommendations'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import Backdrop from 'components/utils/Backdrop'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { urlHashDatePicker } from 'constants/url'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import usePrevious from 'hooks/usePrevious'
import { nonNullable } from 'lib/array/arrayUtils'
import { generateRoomOccupants, isValidOccupancy, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { hasOfferExpired } from 'lib/offer/offerUtils'
import { multiRoomEnabled } from 'selectors/featuresSelectors'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import ImmersiveSaveButtonPortal from 'tripPlanner/components/ImmersiveHeader/ImmersiveSaveButtonPortal'
import { isFlightForceBundled } from 'lib/offer/isFlightForceBundled'
import OfferAirportSelect from '../Filters/Airport/OfferAirportSelect'
import OfferCalendarDatePickerSelect from '../Filters/CalendarPicker/OfferCalendarDatePickerSelect'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import OfferDurationTabs from '../Filters/DurationTabs/OfferDurationTabs'
import MultipleRoomsBookingContent from '../MultipleRoomsBooking/MultipleRoomsBookingContent'
import OfferIntro from './FlashHotelIntro'
import FlashHotelSidebarFilters from './FlashHotelSidebarFilters'
import FlashHotelHeader from './FlashHotelStickyHeader'
import HotelOfferPageStickyFooter from '../HotelOfferPageStickyFooter'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LuxPlusNotificationBanner from 'luxPlus/components/Banners/LuxPlusNotificationBanner'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import FlashHotelPackageOptionsVariant from './FlashHotelMobileVariant/FlashHotelPackageOptionsVariant'
import AccommodationOfferPageStickyFooter from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyFooter'
import SavePromptHotel from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptHotel'
import { encodeSearchParams } from 'lib/search/searchUtils'
import useShareModalConfig from 'hooks/useShareModalConfig'
import WalledContent from 'components/Common/WalledContent'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import AccommodationOfferPageStickyHeaderMobile from 'components/OfferPage/Accommodation/AccommodationOfferPageStickyHeaderMobile'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import { getCheapestSelectedPackageOptionWithDefault, getCheckoutDate, isAvailableForPurchase, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import { useLocation } from 'react-router'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import FlashMobileStickyHeaderFilters from './FlashHotelMobileVariant/FlashMobileStickyHeaderFilters'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import MobileMultipleRoomsBookingContent from '../MultipleRoomsBooking/Mobile/MobileMultipleRoomsBookingContent'
import { useShowBusinessTravellerSelectionModal } from 'businessTraveller/hooks/useShowBusinessTravellerSelectionModal'

const RelativeBlock = styled.div`
  position: relative;
`

const StyledFlashHotelStickyHeader = styled(FlashHotelHeader)`
  position: relative;

  &.hideOnMobile.hideOnMobile {
    display: none;

    ${mediaQueryUp.tablet} {
      display: flex;
      visibility: visible;
    }
  }
`

const PackageOptionsPane = styled(Pane)`
  padding: ${rem(20)} 0;
`

const DesktopFilterBlock = styled(Group)`
  &.open {
    position: relative;
    z-index: 3;
  }
`

const StyledOfferCapacityWithAgesSelect = styled(OfferCapacityWithAgesSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferCalendarDatePickerSelect = styled(OfferCalendarDatePickerSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`

const StyledOfferAirportSelect = styled(OfferAirportSelect)`
  flex-grow: 1;
  max-width: ${rem(250)};
`
interface Props {
  offer: App.HotelOffer;
  airport?: App.Airport;
  duration: number;
  activeFilter?: OfferPageFilterType;
  activeHeaderFilter?: OfferPageFilterType;
  rooms?: Array<App.RoomOccupants>;
  checkInDate?: string;
  checkOutDate?: string;
  flights?: boolean;
  selectedPackage?: App.Package;
  availableForPurchase?: boolean;
  bestPackage: OfferPackageOptionView;
  room?: App.PackageRoomType;
  selectedTravellerEmployeeIds?: Array<string>;
  selectedTravellerGuestCount?: number;
  currentBusinessAccount?: App.BusinessTraveller.CurrentBusinessAccount;
}

/**
 * Until there is a formal way to set a video to auto play on the offer itself
 * We'll see a magic list of offer ids for now
 */
const magicAutoPlayVideoOfferIds = new Set(['006RF00000CoK6HYAV', '006RF00000DX4JtYAL'])

const fallbackRooms = [generateRoomOccupants()]

function FlashHotelContent(props: Props) {
  const {
    offer,
    airport,
    duration,
    activeFilter,
    rooms = fallbackRooms,
    selectedPackage,
    checkInDate,
    checkOutDate,
    availableForPurchase,
    bestPackage,
    room,
    activeHeaderFilter,
    selectedTravellerEmployeeIds,
    selectedTravellerGuestCount,
    currentBusinessAccount,
  } = props

  const location = useLocation()
  const { originAirports, airports } = useContext(GeoContext)
  const luxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const headlessMode = useAppSelector(state => state.system.headlessMode)
  const isMultiRoomEnabled = useAppSelector(state => multiRoomEnabled(state, offer))
  const prevPath = useAppSelector(state => state.routeHistory.prevPath)
  const isMobileDevice = useScreenSizeOnly('mobile')

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(!!checkInDate)

  const { pricing, pricingWithFlights } = bestPackage
  const shouldShowDiscountPercent = pricing.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = pricingWithFlights && pricingWithFlights.discountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = !offer?.isDiscountPillHidden && (shouldShowDiscountPercent && !offer.bundledWithFlightsOnly) || (offer.bundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  const loggedOutSavedPercent = showDiscountPill ? offer.bundledWithFlightsOnly ? pricingWithFlights?.discountPercent : pricing.discountPercent : undefined

  const dispatch = useAppDispatch()
  const stateDispatch = useOfferPageDispatch()
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousRooms = usePrevious(rooms)
  const previousCheckIn = usePrevious(checkInDate)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.RoomOccupants> | null>(null)
  const lastFetchedAirport = useRef<App.Airport | null>(null)
  const desktopFiltersWrapperRef = useRef<HTMLDivElement>(null)
  const desktopFilterBlockRef = useRef<HTMLDivElement>(null)
  const isMultiRoomBooking = isMultiRoomEnabled && rooms.length > 1
  const [stickyFooterVisible, setIsStickyFooterVisible] = useState<boolean>(true)

  // useDefaultMultipleRoomPackages()

  const onStickyFooterToggle = useCallback((visible: boolean) => {
    setIsStickyFooterVisible(visible)
  }, [])

  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()
  useIdleTimeReporter(IdleTimeSource.FlashHotel)

  useShowBusinessTravellerSelectionModal({
    selectedTravellerEmployeeIds,
    selectedTravellerGuestCount,
    currentBusinessAccount,
    routerLocationSearch: location.search,
    stateDispatch,
  })

  const queryString = useMemo(() => {
    return encodeSearchParams({
      urlSearch: location.search,
      dates: {
        checkIn: checkInDate,
        checkOut: checkOutDate,
      },
      rooms,
    }).toString()
  }, [checkInDate, checkOutDate, rooms, location.search])

  // Need to pass the context state above the tree
  useEffect(() => {
    dispatch({
      type: SET_OFFER_CHECK_IN_CHECK_OUT,
      payload: {
        offerId: offer.id,
        checkIn: checkInDate,
        checkOut: checkOutDate,
        duration,
        rooms,
      },
    })

    return
  }, [dispatch, offer.id, checkInDate, duration, rooms, checkOutDate])

  useEffect(() => {
    // clear the check in/check out on unmount
    return () => {
      dispatch({
        type: CLEAR_OFFER_CHECK_IN_CHECK_OUT,
        payload: {
          offerId: offer.id,
        },
      })
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(fetchRelatedTravelItems(offer, checkInDate, checkOutDate, rooms))
  }, [dispatch, offer, checkInDate, rooms, checkOutDate])

  useMountedEffect(() => {
    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousRooms !== rooms ||
      previousCheckIn !== checkInDate
  }, [rooms, checkInDate])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => {
    requestAnimationFrame(() => {
      packageOptionsAnchor.current?.scrollIntoView({ behavior: 'auto' })
    })
  }, [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToFilters = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      requestAnimationFrame(() => {
        desktopFilterBlockRef.current?.scrollIntoView({ behavior: 'auto' })
      })
    }
  }, [isTabletScreenSizeAndUp])

  const fetchCalendarData = useCallback(() => {
    if (!offer.packages.length || !isValidOccupancy(...rooms)) {
      // if we have any invalid ages or no packages, don't try fetch - it'll fail anyway
      return
    }

    if (isMultiRoomBooking) {
      dispatch(fetchCalendarsByOccupancy(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    }

    if (isMobileDevice || !isMultiRoomBooking) {
      // 'new mobile hotel offer page' doesn't support per room multi room booking in the modals
      // So we still have to fetch the combined calendar in these circumstances as well
      // the main page will still use the existing multi room if they wish to do it there
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: nonNullable([selectedPackage as App.HotelPackage ?? offer.lowestPricePackage]),
      }))
      dispatch(fetchCalendars(offer.id, {
        occupants: rooms,
        flightOrigin: airport?.code,
        packages: offer.packages.filter(p => p.duration === duration),
      }))
    }
    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport ?? null
  }, [airport, isMultiRoomBooking, dispatch, duration, offer, rooms, selectedPackage, isMobileDevice])

  useEffect(() => {
    const hasRooms = rooms !== fallbackRooms
    const hasDate = !!checkInDate

    if (previousFilter === 'capacity' &&
      !hasRooms &&
      !activeFilter
    ) {
      autoScrollToFilters()
      scrollAfterFilterClose.current = false
    }

    if (activeFilter) {
      autoScrollToFilters()
    }

    if (previousFilter && !activeFilter && hasRooms && hasDate) {
      if (scrollAfterFilterClose.current) {
        if (room) {
          window.location.hash = room.id
        } else {
          autoScrollToFilters()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if ((activeFilter === 'checkin' || activeHeaderFilter === 'checkin') && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else {
      const airportChanged = lastFetchedAirport.current !== airport
      const roomsChanged = lastFetchedRooms.current !== rooms
      const requiresNewCalendar = isMultiRoomBooking ? airportChanged || roomsChanged : viewedDates && (airportChanged || roomsChanged)
      if (requiresNewCalendar) {
        // rooms/airport has changed, all loaded calendars are no longer valid as we load calendars for a specific
        // combinations of flight origin (flight prices) and rooms (availability), so clear them

        // if multiple room booking is available and we changed the rooms,
        // don't clear the previous data, just update for the new changed
        // rooms (is filtered inside fetchCalendarsByOccupancy action)
        if (!isMultiRoomBooking || isMobileDevice) {
          dispatch(clearBookingCalendar())
        }
        fetchCalendarData()
      }
    }
  }, [activeFilter, activeHeaderFilter, fetchCalendarData]) // eslint-disable-line react-hooks/exhaustive-deps

  useMountedEffect(() => {
    stateDispatch({ type: OfferPageActions.setOffer, offer })
  }, [offer])

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
  // eslint-disable-next-line
  }, [offer.packages])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToFilters()
    }
  }, [duration])

  useMountedEffect(() => {
    if (airport) {
      // effectively an 'on airport change' event
      dispatch(setRecentlyUsedAirportCode(airport.code))
      dispatch(fetchOfferFlightPrice(offer.id, airport.code))
    }
  }, [airport])

  useEffect(() => {
    if (location.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...location, search: queryString }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const occupancyNumber = useMemo(() => sumUpOccupancies(rooms), [rooms])

  const occupancyLimits = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate?.capacities ?? [])
  }, [offer])

  const isValidRoomOccupancy = offer.bundledWithFlightsOnly || occupancyNumber < 10
  const destinationAirport = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const showFlightInformation = offer.offerFlightsEnabled && destinationAirport && (originAirports.length > 1 || (originAirports.length > 0 && destinationAirport.code !== airport?.code)) && isValidRoomOccupancy

  const hideSoldOutOnMobile = !availableForPurchase && config.LERE_EXPIRED_DEALS_V2_ENABLED

  const shareModalConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ') ?? '',
    type: 'offer',
    utmCampaign: 'webshare',
    queryString,
    offerType: 'hotel',
  })

  // Do not show the Expired recommendation when it comes from my escapes
  const shouldShowLere = useMemo(() => hasOfferExpired(offer) && !prevPath.includes('/my-escapes/order'), [offer, prevPath])

  const isNewMobileMultipleRoomEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croNewMobileMultipleRoom)

  const showNewMobileMultipleRoom = isMobileDevice && isNewMobileMultipleRoomEnabled
  return (
    <ProductPaletteProvider product={offer}>
      <ImmersiveSaveButtonPortal>
        <BookmarkButton offer={offer} />
      </ImmersiveSaveButtonPortal>
      <TripGuard>
        <SavePromptHotel offer={offer} />
      </TripGuard>
      <RelativeBlock onSubmit={(e) => e.preventDefault()}>
        {isMobileDevice && <AccommodationOfferPageStickyHeaderMobile>
          <FlashMobileStickyHeaderFilters />
        </AccommodationOfferPageStickyHeaderMobile>}
        <CSSBreakpoint min="tablet">
          <StyledFlashHotelStickyHeader
            offer={offer}
            onViewPackageOptions={scrollToPackageOptions}
            className={cn({ hideOnMobile: hideSoldOutOnMobile })}
          >
            {!headlessMode && <DesktopRelatedTravelItems offer={offer} />}
          </StyledFlashHotelStickyHeader>
        </CSSBreakpoint>
        {luxPlusEnabled && !!offer.luxPlus?.access &&
          <LuxPlusNotificationBanner>
            {offer.luxPlus.access === 'earlyAccess' && 'Members get early access to this offer before the general public and more'}
            {offer.luxPlus.access === 'memberOnly' && 'You get exclusive access to this member-only offer'}
          </LuxPlusNotificationBanner>}
        {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
          <Breakpoint max="mobile">
            <FlashHotelSidebarFilters
              capacities={occupancyLimits}
            />
          </Breakpoint>
        </WalledContent>}
        {shouldShowLere && (
          <div>
            <ExpiredDealsLereRecommendations hideOffers={!!activeFilter} />
          </div>
        )}
        <OfferPageGallery
          offer={offer}
          shareModalConfig={shareModalConfig}
          autoPlayVideo={magicAutoPlayVideoOfferIds.has(offer.id) || !!offer.isVideoVimeoHero}
        />
        <Pane>
          <OfferIntro
            onViewPackageOptions={scrollToPackageOptions}
            flightDestination={destinationAirport}
            shareModalConfig={shareModalConfig}
          />
        </Pane>
        <ScrollAnchor ref={packageOptionsAnchor} />
        <SoftOfferPageWalledGarden>
          <PackageOptionsPane id="Package-Options">
            <VerticalSpacer gap={32}>
              {!isMobileDevice && <LayoutContainer>
                <VerticalSpacer gap={32}>
                  {availableForPurchase && <WalledContent enforced={offer.walledGarden}>
                    <CSSBreakpoint min="tablet">
                      <ScrollAnchor ref={desktopFilterBlockRef} vOffset={rem(12)} />
                      <DesktopFilterBlock ref={desktopFiltersWrapperRef} fullWidth direction="horizontal" gap={8} horizontalAlign="center" wrap="wrap" className={cn({ open: !!activeFilter })}>
                        <StyledOfferCapacityWithAgesSelect
                          boundaryRef={desktopFiltersWrapperRef}
                          capacities={occupancyLimits}
                          disableMultiRoom={isFlightForceBundled(offer)}
                        />
                        <StyledOfferCalendarDatePickerSelect boundaryRef={desktopFiltersWrapperRef} />
                        {showFlightInformation && <StyledOfferAirportSelect />}
                        <Backdrop visible={!!activeFilter} data-testid="filter-backdrop" />
                      </DesktopFilterBlock>
                    </CSSBreakpoint>
                  </WalledContent>}
                  <VerticalSpacer gap={8}>
                    <Subtitle variant="subtitle3" align="center">
                      Select duration
                    </Subtitle>
                    <OfferDurationTabs offerId={offer.id} />
                  </VerticalSpacer>
                </VerticalSpacer>
              </LayoutContainer>}
              {!isMultiRoomBooking && offer.packages.length > 0 && <>
                {!isMobileDevice && <PackageOptions
                  destinationAirport={destinationAirport}
                  onStickyFooterToggle={onStickyFooterToggle}
                />}
                {isMobileDevice && <FlashHotelPackageOptionsVariant />}
              </> }
              {isMultiRoomBooking && !showNewMobileMultipleRoom &&
                <MultipleRoomsBookingContent
                  onStickyFooterToggle={onStickyFooterToggle}
                />}
              {isMultiRoomBooking && showNewMobileMultipleRoom &&
                <MobileMultipleRoomsBookingContent />}
            </VerticalSpacer>
          </PackageOptionsPane>
          {isMobileDevice && availableForPurchase && !(isMultiRoomBooking && showNewMobileMultipleRoom) && <AccommodationOfferPageStickyFooter />}
          {!isMobileDevice && availableForPurchase && <HotelOfferPageStickyFooter
            visible={stickyFooterVisible}
            lowestPricePackage={offer.lowestPricePackage ?? offer.packages[0]}
            onScrollTo={scrollToPackageOptions}
            offer={offer}
          />}
        </SoftOfferPageWalledGarden>
      </RelativeBlock>
      <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    </ProductPaletteProvider>
  )
}

type MappedProps = Pick<Props,
  'airport' |
  'duration' |
  'activeFilter' |
  'rooms' |
  'selectedPackage' |
  'checkInDate' |
  'checkOutDate' |
  'flights' |
  'availableForPurchase' |
  'bestPackage' |
  'room' |
  'activeHeaderFilter' |
  'selectedTravellerEmployeeIds' |
  'selectedTravellerGuestCount' |
  'currentBusinessAccount'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  airport: state.airport,
  duration: state.duration,
  activeFilter: state.activeFilter,
  rooms: state.rooms,
  selectedPackage: state.package,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  flights: state.flights,
  room: state.room,
  availableForPurchase: isAvailableForPurchase(state, appState),
  bestPackage: getCheapestSelectedPackageOptionWithDefault(appState, state),
  activeHeaderFilter: state.activeHeaderFilter,
  selectedTravellerEmployeeIds: appState.businessTraveller.selectedTravellerEmployeeIds,
  selectedTravellerGuestCount: appState.businessTraveller.selectedTravellerGuestCount,
  currentBusinessAccount: appState.businessTraveller.currentBusinessAccount,
}), FlashHotelContent)
